/**
 * Die Modellseite V2 (Produktmodelle 002).
 *
 * WAS SICH GEAENDERT HAT, UND WARUM.
 *
 * Die Seite begann mit „Variantendimensionen", „freien Achsen" und
 * „gekoppelten Eigenschaften" -- einem Konstruktionswerkzeug. Wer HB200-U
 * oeffnete, erfuhr zuerst, wie man eine Achse anlegt, und erst weit unten,
 * welche vier Leuchten es gibt und welcher davon was fehlt.
 *
 * Jetzt gilt dieselbe Ordnung wie in der Produktakte: ein stabiler Kopf mit
 * Kennzahlen, darunter Reiter.
 *
 *   Uebersicht          Welche Ausfuehrungen gibt es, und wie ist ihr Zustand?
 *   Gemeinsame Daten    Was gilt fuer alle -- und wer weicht ab?
 *   Ausfuehrungen       Worin unterscheiden sie sich? Hier liegt auch der
 *                       Achseneditor, eingeklappt.
 *   Texte & Dokumente   Was an der Baureihe haengt.
 *   Verlauf             Was wann geaendert wurde.
 *
 * Die Fachlogik darunter bleibt: dieselben Editoren fuer Achsen, Varianten,
 * Merkmale, Tarifnummer, Verpackung, Texte und Dokumente -- nur nicht mehr
 * als Einstieg.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import type {
  AdoptCommonValueRequest, AdoptFromArticlePreview, DropOwnEvidence, DropOwnPreview,
  MetaResponse, ModelCommonDataResponse, ModelCommonPackaging,
  ModelCommonProperty, ModelExecutionStatus, ModelHistoryResponse, ModelOverviewAxis,
  ModelOverviewResponse, ModelResolvedValue, ProductModelDetail, PropertyDefinition,
  PropertyDefinitionUsage, PropertyGroup, PropertyValue, PropertyValueType,
} from "@homebase/shared";
import { PROPERTY_GROUPS } from "@homebase/shared";
import { api } from "../lib/api.ts";
import { useI18n, type I18n } from "../i18n/index.tsx";
import { errorText } from "../i18n/errors.ts";
import { categoryLabel, familyLabel, modelLabel } from "../i18n/terminology.ts";
import { ErrorState, LoadingState } from "./States.tsx";
import { RoleBadge } from "./Badges.tsx";
import { ProductSubnav } from "./ProductSubnav.tsx";
import { ModelDimensions, ModelVariantList, valueLabel } from "./ModelVariants.tsx";
import { ModelTexts } from "./ModelTexts.tsx";
import { ProductProperties, displayValue, formatNumber } from "./ProductProperties.tsx";
import { propertyLabel } from "../i18n/terminology.ts";
import { ModelLogistics } from "./ModelLogistics.tsx";
import { ArticleDocuments, type DokumenteSteuerung } from "./ArticleDocuments.tsx";
import { LoeschDialog, LoesenDialog, VerwerfenDialog, ZuordnungDialog,
         type AusfuehrungsKontext } from "./ModelAssign.tsx";
import "./ProductModels.css";
import "./ModelPage.css";

const MODEL_TABS = ["overview", "common", "executions", "content", "history"] as const;
type ModelTab = (typeof MODEL_TABS)[number];

/** Masse und Gewicht -- die Karte, die die Verpackung braucht. */
const MASSE = new Set(["length", "width", "height", "diameter", "cutout_size", "weight"]);

/** Was an einer belegten Ausfuehrung moeglich ist (Produktmodelle 002B). */
export interface Ausfuehrungsaktionen {
  readonly aendern: (variantId: number) => void;
  readonly loesen: (variantId: number) => void;
  /** Nur mit dem Recht zur endgueltigen Loeschung. */
  readonly verwerfen: ((articleId: number) => void) | null;
}

export interface ModelRights {
  readonly mayWriteModels: boolean;
  readonly mayWriteLogistics: boolean;
  readonly mayWriteTexts: boolean;
  readonly mayReleaseTexts: boolean;
  readonly mayWriteProps: boolean;
  readonly mayConfirmProps: boolean;
  readonly mayWriteDocuments: boolean;
  /** article.write -- einen neuen Artikel aus einer Ausfuehrung anlegen. */
  readonly mayCreateArticle: boolean;
  /** model.write UND article.delete -- ein Modell endgueltig loeschen. */
  readonly mayDeleteModels: boolean;
  /** system.manage -- den Merkmalskatalog pflegen (Produktmodelle 003 V2). */
  readonly mayManageCatalog: boolean;
}

export function ModelDetailPanel({ id, meta, rights, onClose, onListChanged, onOpenArticle, onNeuerArtikel, onDeleted }: {
  id: number;
  meta: MetaResponse;
  rights: ModelRights;
  onClose: () => void;
  onListChanged: () => void;
  onOpenArticle: (id: number) => void;
  /** Den Produktassistenten mit dem Kontext einer Ausfuehrung oeffnen. */
  onNeuerArtikel?: (k: AusfuehrungsKontext) => void;
  /** Nach einer endgueltigen Loeschung: Detail schliessen, Liste neu laden. */
  onDeleted?: () => void;
}): React.JSX.Element {
  const i18n = useI18n();
  const t = i18n.t;
  const [model, setModel] = useState<ProductModelDetail | null>(null);
  const [overview, setOverview] = useState<ModelOverviewResponse | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [tab, setTab] = useState<ModelTab>("overview");
  const [stamm, setStamm] = useState(false);
  /* Die Ausfuehrung, der gerade ein Artikel zugeordnet wird -- aus Matrix
     ODER Ausfuehrungsreiter, derselbe Dialog. */
  const [zuordnen, setZuordnen] = useState<number | null>(null);
  /* Produktmodelle 002B: Zuordnung loesen, Fehlanlage verwerfen. */
  const [loesen, setLoesen] = useState<number | null>(null);
  const [verwerfen, setVerwerfen] = useState<number | null>(null);
  const [loeschen, setLoeschen] = useState(false);

  const loadOverview = useCallback(async () => {
    try { setOverview(await api.modelOverview(id)); } catch (e) { setError(e); }
  }, [id]);
  /* Modell und Uebersicht gemeinsam: der Kopf erscheint erst, wenn auch seine
     Kennzahlen und die Matrix stehen -- kein Zwischenzustand mit halbem Inhalt. */
  const load = useCallback(async () => {
    setError(null);
    try {
      const [m, o] = await Promise.all([api.model(id), api.modelOverview(id)]);
      setOverview(o); setModel(m);
    } catch (e) { setError(e); }
  }, [id]);

  useEffect(() => { setModel(null); setOverview(null); void load(); }, [load]);

  useEffect(() => {
    const h = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [onClose]);

  /* Jede Aenderung am Modell kann Achsenrolle, Abdeckung und Kalkulierbarkeit
     aendern -- die Uebersicht wird deshalb immer mitgeladen. */
  const applyChange = useCallback((m: ProductModelDetail) => {
    setModel(m); onListChanged(); void loadOverview();
  }, [onListChanged, loadOverview]);

  /* Nach einer Zuordnung: Modell UND Uebersicht neu -- die Matrix zeigt die
     Artikelnummer sofort, ohne dass jemand die Seite neu laedt. */
  const nachZuordnung = useCallback(async () => {
    setZuordnen(null);
    try {
      const [m, o] = await Promise.all([api.model(id), api.modelOverview(id)]);
      setOverview(o); setModel(m); onListChanged();
    } catch (e) { setError(e); }
  }, [id, onListChanged]);

  /* Die Aktionen einer BELEGTEN Ausfuehrung -- an einer Stelle, fuer Matrix
     und Ausfuehrungsreiter. Ohne Recht gibt es sie nicht. */
  const aktionen: Ausfuehrungsaktionen | null = rights.mayWriteModels ? {
    aendern: setZuordnen,
    loesen: setLoesen,
    verwerfen: rights.mayDeleteModels ? setVerwerfen : null,
  } : null;

  const neuerArtikel = (variantId: number): (() => void) | null => {
    if (!onNeuerArtikel || !model) return null;
    const v = model.variants.find((x) => x.id === variantId);
    if (!v) return null;
    return () => {
      setZuordnen(null);
      onNeuerArtikel({
        modelId: model.id, familyId: model.family.id, categoryId: model.category.id, variantId,
        values: v.values.map((w) => ({ dimensionId: w.dimensionId, valueId: w.valueId })),
        label: `${model.code} · ${v.values.map((w) => valueLabel(i18n, w,
          overview?.axes.find((a) => a.dimensionId === w.dimensionId)?.unit ?? null)).join(" · ")}`,
      });
    };
  };

  return (
    <aside className="detail models__detail mp" aria-label={t("models.detailAria")}>
      <div className="detail__head">
        <div className="mp__kopf">
          <h2 className="detail__title">{model?.name ?? t("models.fallbackTitle")}</h2>
          {model && (
            <p className="mp__einordnung">
              {/*
                * DIESELBE REGEL WIE UEBERALL (§3).
                *
                * Hier stand `model.name !== model.code` -- eine
                * ZWEITE, lokale Fassung derselben Regel, und eine
                * strengere: sie vergleicht zeichengenau und haelt
                * „HB200-U" und „hb200-u " fuer zwei Angaben.
                * `modelLabel` normalisiert.
                */}
              {modelLabel(model.code, model.name).name !== null && (
                <span className="mp__name"><code>{model.code}</code> · </span>
              )}
              {familyLabel(i18n, model.family.code, model.family.name)}
              {" · "}
              {categoryLabel(i18n, model.category.code, model.category.name)}
            </p>
          )}
          {/* Bestaetigt ist der Normalfall und steht nicht da. Ein Vorschlag sagt es. */}
          {model && model.provenance !== "bestaetigt" && (
            <span className="mp__vorschlag">
              <span className="badge badge--draft">{t("mp.proposal")}</span>
              {rights.mayWriteModels && (
                <button className="btn btn--small"
                  onClick={async () => {
                    try { applyChange(await api.updateModel(model.id, { provenance: "bestaetigt" })); }
                    catch (e) { setError(e); }
                  }}>{t("models.confirm")}</button>
              )}
            </span>
          )}
        </div>
        {model && rights.mayWriteModels && !stamm && (
          <button className="btn mp__stammknopf" onClick={() => setStamm(true)}>{t("mp.editMaster")}</button>
        )}
        <button className="btn" onClick={onClose} aria-label={t("common.close")}>{t("common.close")}</button>
      </div>

      {error !== null && <ErrorState message={errorText(i18n, error)} />}
      {!model && error === null && <LoadingState />}

      {model && (
        <div className="detail__body">
          {stamm && (
            <Stammdaten model={model} i18n={i18n} mayDelete={rights.mayDeleteModels}
              onLoeschen={() => { setStamm(false); setLoeschen(true); }}
              onDone={(m) => { setStamm(false); if (m) applyChange(m); }} onError={setError} />
          )}
          {loeschen && (
            <LoeschDialog modelId={model.id} i18n={i18n} onClose={() => setLoeschen(false)}
              onDeleted={() => { setLoeschen(false); onDeleted?.(); }} />
          )}
          <Kennzahlen overview={overview} i18n={i18n} onOffene={() => setTab("executions")} />
          {zuordnen !== null && (
            <ZuordnungDialog variantId={zuordnen} overview={overview} meta={meta} i18n={i18n}
              mayCreateArticle={rights.mayCreateArticle} onNeuerArtikel={neuerArtikel(zuordnen)}
              onDone={() => void nachZuordnung()} onClose={() => setZuordnen(null)} />
          )}
          {loesen !== null && (
            <LoesenDialog variantId={loesen} i18n={i18n}
              onDone={() => { setLoesen(null); void nachZuordnung(); }}
              onClose={() => setLoesen(null)} />
          )}
          {verwerfen !== null && (
            <VerwerfenDialog articleId={verwerfen} i18n={i18n}
              onDone={() => { setVerwerfen(null); void nachZuordnung(); }}
              onClose={() => setVerwerfen(null)} />
          )}
          <ProductSubnav sections={MODEL_TABS} current={tab} onSelect={setTab} prefix="mtab" />

          {tab === "overview" && (overview
            ? <UebersichtTab overview={overview} meta={meta} i18n={i18n} onOpenArticle={onOpenArticle}
                onAssign={rights.mayWriteModels ? setZuordnen : null}
                aktionen={aktionen} />
            : <LoadingState />)}

          {tab === "common" && (
            <GemeinsamTab modelId={model.id} meta={meta} rights={rights} i18n={i18n}
              achsen={(overview?.axes ?? []).map((a) => a.definition)}
              onChanged={() => void loadOverview()} />
          )}

          {tab === "executions" && (
            <AusfuehrungenTab model={model} overview={overview} meta={meta} rights={rights} i18n={i18n}
              onChanged={applyChange} onError={setError} onOpenArticle={onOpenArticle}
              onAssign={rights.mayWriteModels ? setZuordnen : null} aktionen={aktionen} />
          )}

          {tab === "content" && (
            <InhaltTab modelId={model.id} meta={meta} rights={rights} i18n={i18n} />
          )}

          {tab === "history" && <VerlaufTab modelId={model.id} i18n={i18n} />}
        </div>
      )}
    </aside>
  );
}

// ── Kopf ────────────────────────────────────────────────────────────────────

/**
 * Die Kennzahlen unter dem Titel -- in einer Zeile, was ein Anwender zuerst
 * wissen will. Kalkulation und Preis nur mit Einkaufsrecht; ohne es steht
 * dort nichts, statt „0 kalkuliert".
 */
function Kennzahlen({ overview, i18n, onOffene }: {
  overview: ModelOverviewResponse | null; i18n: I18n; onOffene: () => void;
}): React.JSX.Element | null {
  const t = i18n.t;
  if (!overview) return null;
  const alle = overview.executions;
  const mitArtikel = alle.filter((e) => e.article !== null);
  const beschaffung = mitArtikel.filter((e) => e.purchasing !== null);
  const kalkuliert = beschaffung.filter((e) => e.purchasing!.basis.latest?.complete).length;
  const frei = beschaffung.filter((e) => e.purchasing!.released !== null).length;
  /* Fachlich vollstaendig: Technik, Lieferant, EXW, Zoll, Verpackung, kalkulierbar. */
  const vollstaendig = beschaffung.filter((e) => e.technikMissing.length === 0
    && e.purchasing!.suppliers.length > 0 && e.purchasing!.exw !== null
    && e.purchasing!.tariff !== null && e.purchasing!.packaging !== null
    && e.purchasing!.basis.calculable).length;
  return (
    <p className="mp__kennzahlen" data-testid="mp-kennzahlen">
      <span><strong>{alle.length}</strong> {t("mp.kpiExecutions")}</span>
      <span><strong>{mitArtikel.length}</strong> {t("mp.kpiSkus")}</span>
      {/* Offene Zuordnungen -- kein Fehler, sondern ein Arbeitsstand. Der
          Sprung fuehrt zu den Ausfuehrungen, an denen er erledigt wird. */}
      {alle.length > 0 && alle.length > mitArtikel.length && (
        <button type="button" className="mp__offen" data-testid="mp-offen" onClick={onOffene}>
          {t("mp.kpiOpen", { n: alle.length - mitArtikel.length })}
        </button>
      )}
      {alle.length > 0 && alle.length === mitArtikel.length && (
        <span className="mp__vollstaendig" data-testid="mp-vollstaendig">
          {t("mp.kpiAssigned", { n: mitArtikel.length, total: alle.length })}
        </span>
      )}
      {/*
        * ZUORDNUNG IST NICHT VOLLSTAENDIGKEIT (Produktmodelle 002B).
        *
        * „Zuordnung vollständig" las sich wie „Produktmodell fertig" -- an
        * TP154 waren drei Ausfuehrungen verknuepft und zwei davon leer.
        * Beides steht jetzt getrennt da: wie viele verknuepft sind, und wie
        * viele fachlich vollstaendig sind (Technik, Einkauf, Zoll,
        * Verpackung, kalkulierbar).
        */}
      {beschaffung.length > 0 && (
        <span data-testid="mp-vollstaendigkeit"
          className={vollstaendig === alle.length ? "mp__vollstaendig"
            : vollstaendig < alle.length ? "mp__kennzahl--offen" : undefined}>
          <strong>{vollstaendig}</strong> {t("mp.kpiCompleteOf", { total: alle.length })}
        </span>
      )}
      {/*
        * ZWEI ZAHLEN, NICHT VIER (UI/UX 001).
        *
        * Hier stand zusaetzlich „n Kalkulationsbasis unvollstaendig". Das ist
        * die Gegenzahl zu „x von y fachlich vollstaendig" -- dieselbe Aussage
        * zweimal, einmal positiv und einmal negativ formuliert. WELCHE
        * Angabe fehlt, sagt ohnehin nur die Statustabelle darunter, und die
        * sagt es je Ausfuehrung.
        */}
      {beschaffung.length > 0 && (<>
        <span><strong>{kalkuliert}</strong> {t("mp.kpiCalculated")}</span>
        <span><strong>{frei}</strong> {t("mp.kpiReleased")}</span>
      </>)}
    </p>
  );
}

/**
 * Stammdaten bearbeiten -- Name und Notiz.
 *
 * Modellcode und Produktfamilie bleiben hier bewusst lesend. Der Code steht
 * im Grundlagenabdruck jedes freigegebenen Modelltexts und in der Notiz jeder
 * Modellfreigabe; ihn umzubenennen ist eine Entscheidung, keine Korrektur. Ein
 * Familienwechsel verlangt, dass alle zugeordneten Artikel mitkoennen -- der
 * Server prueft das inzwischen, die Oberflaeche bietet ihn trotzdem noch
 * nicht an. Ein Modellbild hat keine Ablage (BLOCK 003).
 */
function Stammdaten({ model, i18n, mayDelete, onLoeschen, onDone, onError }: {
  model: ProductModelDetail; i18n: I18n; mayDelete: boolean; onLoeschen: () => void;
  onDone: (m: ProductModelDetail | null) => void; onError: (e: unknown) => void;
}): React.JSX.Element {
  const t = i18n.t;
  const [name, setName] = useState(model.name);
  const [note, setNote] = useState(model.note ?? "");
  const [busy, setBusy] = useState(false);
  const aenderung = name.trim() !== model.name || (note.trim() === "" ? null : note.trim()) !== (model.note ?? null);
  async function speichern(): Promise<void> {
    setBusy(true);
    try {
      onDone(await api.updateModel(model.id, {
        ...(name.trim() !== model.name ? { name: name.trim() } : {}),
        ...((note.trim() === "" ? null : note.trim()) !== (model.note ?? null)
          ? { note: note.trim() === "" ? null : note.trim() } : {}),
      }));
    } catch (e) { onError(e); } finally { setBusy(false); }
  }
  return (
    <div className="models__form models__form--wide mp__stamm">
      {/* DIESELBEN FELDER WIE IN DER ANLAGE (UI/UX 001): gleiche Klassen,
          gleiche Groessen, gleiche Reihenfolge. Zwei Stile fuer dasselbe
          Feld waren der eigentliche Fehler, nicht die Feldhoehe. */}
      <div className="feldgruppe">
        <h4 className="feldgruppe__titel">{t("models.sectionModel")}</h4>
        <label className="feld">
          <span className="feld__label">{t("models.name")}</span>
          <input className="feld__input mp__stammname" data-testid="ux-modellname" value={name}
                 maxLength={160} onChange={(e) => setName(e.target.value)} />
        </label>
        <label className="feld feld--lang">
          <span className="feld__label">{t("mp.note")}</span>
          <textarea className="feld__input feld__mehrzeilig mp__stammnotiz" data-testid="ux-modellnotiz"
                    rows={2} value={note} maxLength={2000} onChange={(e) => setNote(e.target.value)} />
        </label>
      </div>
      <p className="models__note">
        {t("mp.codeReadonly", { code: model.code })} {t("mp.familyReadonly")}
      </p>
      <div className="models__formactions">
        <button className="btn btn--primary mp__stammspeichern" disabled={busy || !aenderung || name.trim() === ""}
          onClick={() => void speichern()}>{t("common.save")}</button>
        <button className="btn" onClick={() => onDone(null)}>{t("common.cancel")}</button>
      </div>
      {/* Destruktives getrennt von der Bearbeitung -- eingeklappt, leise,
          und erst nach einer Vorschau wirksam. */}
      {mayDelete && (
        <details className="mp__weitere" data-testid="mp-weitere">
          <summary>{t("mp.moreActions")}</summary>
          <button className="btn btn--small mp__loeschenknopf" onClick={onLoeschen}>{t("mz.deleteOpen")}</button>
        </details>
      )}
    </div>
  );
}

// ── Werte anzeigen ──────────────────────────────────────────────────────────

function wertText(def: PropertyDefinition | undefined, werte: readonly ModelResolvedValue[], i18n: I18n): string {
  if (werte.length === 0) return "—";
  if (!def) return werte.map((w) => w.code ?? w.text ?? w.num ?? "").join(" · ");
  return werte.map((w) => displayValue(def, w as unknown as PropertyValue, i18n, false)).join(" · ");
}

function artikelNummer(overview: ModelOverviewResponse, articleId: number): string {
  return overview.executions.find((e) => e.article?.id === articleId)?.article?.articleNumber ?? String(articleId);
}

// ── Uebersicht ──────────────────────────────────────────────────────────────

function UebersichtTab({ overview, meta, i18n, onOpenArticle, onAssign, aktionen }: {
  overview: ModelOverviewResponse; meta: MetaResponse; i18n: I18n; onOpenArticle: (id: number) => void;
  onAssign: ((variantId: number) => void) | null;
  aktionen: Ausfuehrungsaktionen | null;
}): React.JSX.Element {
  const t = i18n.t;
  return (
    <div className="mp__tab">
      <Ausfuehrungsmatrix overview={overview} meta={meta} i18n={i18n} onOpenArticle={onOpenArticle} onAssign={onAssign} aktionen={aktionen} />
      <GemeinsameAchsen overview={overview} i18n={i18n} />
      <h3 className="detail__section">{t("mp.statusTitle")}</h3>
      <StatusTabelle overview={overview} i18n={i18n} onOpenArticle={onOpenArticle} />
    </div>
  );
}

/**
 * Was die angelegten Ausfuehrungen GEMEINSAM tragen -- und welche Werte
 * zulaessig sind, ohne dass es eine Ausfuehrung dazu gibt.
 *
 * HB200-U: „Abstrahlwinkel 85° – gilt für alle Ausführungen" und
 * „Weitere verfügbare Optik: 65° – keine Ausführung angelegt". Der Wert 65°
 * bleibt im Datenmodell; er wird nur nicht mehr wie eine belegte Achse
 * gezeigt.
 */
function GemeinsameAchsen({ overview, i18n }: { overview: ModelOverviewResponse; i18n: I18n }): React.JSX.Element | null {
  const t = i18n.t;
  const gemeinsam = overview.axes.filter((a) => a.role === "common");
  const frei = overview.axes.flatMap((a) => a.values.filter((v) => v.variantCount === 0)
    .map((v) => ({ achse: a, wert: v })));
  const ungenutzt = overview.axes.filter((a) => a.role === "unused" && a.values.length === 0);
  if (gemeinsam.length === 0 && frei.length === 0 && ungenutzt.length === 0) return null;
  return (
    <div className="mp__gemeinsam" data-testid="mp-gemeinsam">
      {gemeinsam.map((a) => {
        const belegt = a.values.filter((v) => v.variantCount > 0);
        return (
          <p key={a.dimensionId} className="mp__gemeinsamzeile mp__gemeinsamzeile--achse">
            <span className="mp__gemeinsamlabel">{t("mp.commonLabel")}</span>
            <strong>{i18n.tCode("prop", a.definition, a.definition)}{" "}
              {belegt.map((v) => valueLabel(i18n, v, a.unit)).join(" · ")}</strong>
            <span className="mp__gemeinsamhinweis">{t("mp.appliesToAll")}</span>
          </p>
        );
      })}
      {frei.map(({ achse, wert }) => (
        <p key={`${achse.dimensionId}-${wert.id}`} className="mp__gemeinsamzeile mp__gemeinsamzeile--frei">
          <span className="mp__gemeinsamlabel">{t("mp.furtherValue", { prop: i18n.tCode("prop", achse.definition, achse.definition) })}</span>
          <strong>{valueLabel(i18n, wert, achse.unit)}</strong>
          <span className="mp__gemeinsamhinweis">{t("mp.noExecution")}</span>
        </p>
      ))}
    </div>
  );
}

/**
 * Die Ausfuehrungsmatrix -- nur aus den UNTERSCHEIDENDEN Achsen.
 *
 * Zwei unterscheidende Achsen: Spalten und Zeilen (HB200-U: Farbtemperatur
 * und CRI). Eine dritte und weitere gruppieren. Eine: eine Zeile. Keine:
 * eine Liste. Zeilen und Spalten nennen nur Werte, die eine Ausfuehrung
 * tragen -- ein zulaessiger Wert ohne Ausfuehrung steht darunter, nicht als
 * leere Zeile, die nach einer fehlenden Leuchte aussieht.
 */
function Ausfuehrungsmatrix({ overview, meta, i18n, onOpenArticle, onAssign, aktionen }: {
  overview: ModelOverviewResponse; meta: MetaResponse; i18n: I18n; onOpenArticle: (id: number) => void;
  onAssign: ((variantId: number) => void) | null;
  aktionen: Ausfuehrungsaktionen | null;
}): React.JSX.Element {
  const t = i18n.t;
  const achsen = overview.axes.filter((a) => a.role === "distinguishing");
  const belegt = (a: ModelOverviewAxis) => a.values.filter((v) => v.variantCount > 0);
  const wertVon = (e: ModelExecutionStatus, a: ModelOverviewAxis): number | null =>
    e.values.find((v) => v.dimensionId === a.dimensionId)?.valueId ?? null;

  if (overview.executions.length === 0) {
    return <p className="models__empty">{t("models.noVariants")}</p>;
  }
  if (achsen.length === 0) {
    return (
      <div className="mp__matrixliste">
        {overview.executions.map((e) => (
          <Zelle key={e.variantId} e={e} overview={overview} meta={meta} i18n={i18n} onOpenArticle={onOpenArticle} onAssign={onAssign} aktionen={aktionen} />
        ))}
      </div>
    );
  }
  const spalte = achsen[0]!;
  const zeile = achsen[1] ?? null;
  const gruppenAchsen = achsen.slice(2);
  /* Die vorhandenen Kombinationen der Gruppenachsen -- keine erfundenen. */
  const gruppen = gruppenAchsen.length === 0 ? [[] as number[]]
    : [...new Map(overview.executions.map((e) => {
        const k = gruppenAchsen.map((a) => wertVon(e, a) ?? -1);
        return [k.join(","), k] as const;
      })).values()];

  return (
    <div className="mp__matrix" data-testid="mp-matrix">
      {gruppen.map((g) => (
        <div key={g.join(",") || "alle"} className="mp__matrixgruppe">
          {gruppenAchsen.length > 0 && (
            <p className="mp__matrixgruppentitel">
              {gruppenAchsen.map((a, i) => {
                const v = a.values.find((x) => x.id === g[i]);
                return `${i18n.tCode("prop", a.definition, a.definition)} ${v ? valueLabel(i18n, v, a.unit) : "—"}`;
              }).join(" · ")}
            </p>
          )}
          <div className="mp__matrixrahmen">
            <table className="mp__matrixtabelle">
              <thead>
                <tr>
                  {/*
                    * ── DIE ECKE BENENNT BEIDE ACHSEN (§5) ──────────
                    *
                    * HIER STAND EIN `\u2572` -- das Kreuztabellen-
                    * Zeichen zwischen Zeilen- und Spaltenachse. Es ist
                    * eine Konvention aus gedruckten Tabellen, und sie
                    * hat genau einen Fehler: sie erklaert sich nicht.
                    * In der Abnahme wurde daraus „ein kleiner
                    * diagonaler Strich" -- gelesen als Artefakt, nicht
                    * als Bedeutung.
                    *
                    * Ein Vorleseprogramm machte daraus „Box Drawings
                    * Light Diagonal".
                    *
                    * JETZT STEHEN DIE ACHSEN DA, benannt. Zwei Zeilen
                    * statt eines Zeichens -- und die Angabe ist
                    * dieselbe.
                    */}
                  <th className="mp__matrixecke">
                    {zeile && (
                      <span className="mp__matrixachse">
                        {t("mp.matrixRows", {
                          a: i18n.tCode("prop", zeile.definition, zeile.definition) })}
                      </span>
                    )}
                    <span className="mp__matrixachse">
                      {t("mp.matrixCols", {
                        a: i18n.tCode("prop", spalte.definition, spalte.definition) })}
                    </span>
                  </th>
                  {belegt(spalte).map((sv) => (
                    <th key={sv.id} className="mp__matrixkopf">{valueLabel(i18n, sv, spalte.unit)}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {(zeile ? belegt(zeile) : [null]).map((zv) => (
                  <tr key={zv?.id ?? "eine"}>
                    <th className="mp__matrixzeile">{zv && zeile ? valueLabel(i18n, zv, zeile.unit) : ""}</th>
                    {belegt(spalte).map((sv) => {
                      const treffer = overview.executions.filter((e) =>
                        wertVon(e, spalte) === sv.id
                        && (zeile === null || wertVon(e, zeile) === zv!.id)
                        && gruppenAchsen.every((a, i) => (wertVon(e, a) ?? -1) === g[i]));
                      return (
                        <td key={sv.id} className="mp__matrixzelle">
                          {treffer.length === 0
                            ? <span className="models__none" title={t("mp.noExecutionCell")}>—</span>
                            : treffer.map((e) => (
                                <Zelle key={e.variantId} e={e} overview={overview} meta={meta}
                                  i18n={i18n} onOpenArticle={onOpenArticle} onAssign={onAssign} aktionen={aktionen} />
                              ))}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ))}
    </div>
  );
}

/** Eine Ausfuehrung in der Matrix: Nummer, die Werte, die sie auszeichnen, ihr Zustand. */
function Zelle({ e, overview, meta, i18n, onOpenArticle, onAssign, aktionen }: {
  e: ModelExecutionStatus; overview: ModelOverviewResponse; meta: MetaResponse; i18n: I18n;
  onOpenArticle: (id: number) => void;
  onAssign: ((variantId: number) => void) | null;
  aktionen: Ausfuehrungsaktionen | null;
}): React.JSX.Element {
  const t = i18n.t;
  const achsen = new Set(overview.axes.map((a) => a.definition));
  /* Die Werte, die DIESE Ausfuehrung auszeichnen -- ohne die Achsen selbst,
     die schon Zeile und Spalte sind, und ohne die Lichtfarbe, die nur das
     Wort zur Farbtemperatur ist. */
  const merkmale = e.article === null ? [] : overview.deviations
    .filter((d) => !achsen.has(d.definition) && d.definition !== "light_colour")
    .slice(0, 2)
    .map((d) => {
      const def = meta.propertyDefinitions.find((x) => x.code === d.definition);
      const werte = d.perArticle.find((p) => p.articleId === e.article!.id)?.values ?? [];
      return { code: d.definition, text: wertText(def, werte, i18n) };
    });
  return (
    <div className="mp__zelle" data-testid="mp-zelle">
      {e.article
        ? <button className="models__link mp__zellnummer" onClick={() => onOpenArticle(e.article!.id)}>
            <code>{e.article.articleNumber}</code>
          </button>
        : <>
            {/* HANDLUNGSFAEHIG, NICHT NUR ANZEIGE: die Ausfuehrung sagt, was ihr
                fehlt, und bietet den naechsten Schritt an. */}
            <span className="mp__keinartikel" title={t("models.virtualTitle")}>
              {e.label ? `${e.label} · ` : ""}{t("mp.noArticle")}</span>
            {/* Weggeraeumt heisst nicht vergessen: wer hier liegt, steht da. */}
            {e.trashedArticle && (
              <span className="mp__papierkorb" data-testid="mp-papierkorb">
                {t("mp.inTrash", { number: e.trashedArticle.articleNumber })}
              </span>
            )}
            <span className="mp__zellwert mp__zellwerte">{e.values.map((v) => valueLabel(i18n, v,
              overview.axes.find((a) => a.dimensionId === v.dimensionId)?.unit ?? null)).join(" · ")}</span>
            {onAssign && (
              <button className="btn btn--small mp__zuordnen" data-testid="mp-zuordnen"
                onClick={() => onAssign(e.variantId)}>{t("mz.open")}</button>
            )}
          </>}
      {merkmale.map((m) => <span key={m.code} className="mp__zellwert">{m.text}</span>)}
      {e.article && <StatusLeiste e={e} i18n={i18n} />}
      {/* Die Aktionen einer belegten Ausfuehrung -- eingeklappt, damit die
          Matrix eine Uebersicht bleibt und kein Schalterbrett. */}
      {e.article && aktionen && (
        <details className="mk__aktionen" data-testid="mk-aktionen">
          <summary>{t("mk.actions")}</summary>
          {/*
            * DREI STUFEN, SICHTBAR UNTERSCHIEDEN (UI/UX 001).
            *
            * Oben, was nichts veraendert: den Artikel oeffnen. Darunter die
            * beiden Eingriffe in die Zuordnung. Ganz unten, abgesetzt und
            * als einzige in Warnfarbe, die Loeschung. Vier gleich aussehende
            * Knoepfe nebeneinander waren die eigentliche Gefahr -- nicht die
            * Loeschung selbst.
            */}
          <div className="mk__aktionsliste">
            <button className="btn btn--small mk__oeffnen"
              onClick={() => onOpenArticle(e.article!.id)}>{t("mk.openArticle")}</button>
            <button className="btn btn--small mk__aendern"
              onClick={() => aktionen.aendern(e.variantId)}>{t("mk.change")}</button>
            <button className="btn btn--small mk__loesenknopf"
              onClick={() => aktionen.loesen(e.variantId)}>{t("mk.unassignOpen")}</button>
          </div>
          {aktionen.verwerfen && (
            <div className="mk__aktionsliste mk__aktionsliste--gefahr">
              <button className="btn btn--small btn--danger mk__verwerfenknopf"
                onClick={() => aktionen.verwerfen!(e.article!.id)}>{t("mk.discardOpen")}</button>
            </div>
          )}
        </details>
      )}
    </div>
  );
}

/** Der Zustand einer Ausfuehrung in sechs Zeichen -- ✓ vorhanden, ✕ fehlt. */
function StatusLeiste({ e, i18n }: { e: ModelExecutionStatus; i18n: I18n }): React.JSX.Element {
  const t = i18n.t;
  const p = e.purchasing;
  const punkt = (ok: boolean, label: string, titel?: string): React.JSX.Element => (
    <span className={ok ? "mp__punkt mp__punkt--ok" : "mp__punkt mp__punkt--fehlt"} title={titel}>
      {ok ? "✓" : "✕"} {label}
    </span>
  );
  return (
    <span className="mp__status" data-testid="mp-status">
      {punkt(e.technikMissing.length === 0, t("mp.stTechnik"),
        e.technikMissing.map((c) => i18n.tCode("prop", c, c)).join(", ") || undefined)}
      {p && (<>
        {punkt(p.suppliers.length > 0 && p.exw !== null, t("mp.stEinkauf"))}
        {punkt(p.tariff !== null, t("mp.stZoll"), p.tariff?.inherited ? t("mp.inheritedFromModel") : undefined)}
        {punkt(p.packaging !== null, t("mp.stVerpackung"), p.packaging === "model" ? t("mp.inheritedFromModel") : undefined)}
        {punkt(p.basis.calculable, t("mp.stKalkulation"))}
        <span className="mp__preis">
          {p.released
            ? t("mp.priceReleased", { amount: formatNumber(p.released.amountEur, i18n.lang) })
            : p.basis.latest?.complete && p.basis.latest.listPriceEur
              ? t("mp.priceCalculated", { amount: formatNumber(p.basis.latest.listPriceEur, i18n.lang) })
              : t("mp.priceNone")}
        </span>
      </>)}
    </span>
  );
}

/**
 * Der Zustand je Ausfuehrung als Tabelle -- mit dem, was fehlt, und dem Weg
 * dorthin. Die Luecken kommen aus dem TROCKENLAUF, nicht aus einem alten
 * Stand: „Tarif fehlt" heisst, dass er HEUTE fehlt.
 */
function StatusTabelle({ overview, i18n, onOpenArticle }: {
  overview: ModelOverviewResponse; i18n: I18n; onOpenArticle: (id: number) => void;
}): React.JSX.Element {
  const t = i18n.t;
  const zeilen = overview.executions.filter((e) => e.article !== null);
  const mitEinkauf = zeilen.some((e) => e.purchasing !== null);
  const ja = (ok: boolean): React.JSX.Element =>
    <span className={ok ? "mp__punkt mp__punkt--ok" : "mp__punkt mp__punkt--fehlt"}>{ok ? "✓" : "✕"}</span>;
  const geld = (v: string): string => `${formatNumber(v, i18n.lang)} €`;
  if (zeilen.length === 0) return <p className="models__empty" data-testid="mp-status-leer">{t("mp.statusEmpty")}</p>;
  return (
    <div className="mp__tabellenrahmen">
      <table className="mp__table mp__statustabelle" data-testid="mp-statustabelle">
        <thead>
          <tr>
            <th>{t("models.colVariant")}</th>
            <th>{t("mp.stTechnik")}</th>
            {mitEinkauf && (<>
              <th>{t("mp.colSupplier")}</th>
              <th>{t("mp.colExw")}</th>
              <th>{t("mp.stZoll")}</th>
              <th>{t("mp.stVerpackung")}</th>
              <th>{t("mp.colCalculable")}</th>
              <th>{t("mp.colLatest")}</th>
              <th>{t("mp.colCalculated")}</th>
              <th>{t("mp.colReleased")}</th>
            </>)}
          </tr>
        </thead>
        <tbody>
          {zeilen.map((e) => {
            const p = e.purchasing;
            const luecken = [...e.technikMissing.map((c) => i18n.tCode("prop", c, c)),
                             ...(p?.basis.missing ?? []).map((c) => i18n.tCode("calcInput", c, c))];
            return (
              <tr key={e.variantId} data-testid="mp-statuszeile">
                <td>
                  <button className="models__link" onClick={() => onOpenArticle(e.article!.id)}>
                    <code>{e.article!.articleNumber}</code>
                  </button>
                  {/* Eine ARBEITSLISTE, kein Fliesstext (UI/UX 001): jede
                      fehlende Angabe als eigenes Plaettchen -- abhakbar mit
                      den Augen, statt in einem Satz zu verschwimmen. */}
                  {luecken.length > 0 && (
                    <div className="mp__luecken" data-testid="mp-luecken">
                      <span className="mp__lueckenkopf">{t("mp.missingShort")}</span>
                      {luecken.map((l) => <span key={l} className="mp__luecke">{l}</span>)}
                    </div>
                  )}
                </td>
                <td>{ja(e.technikMissing.length === 0)}</td>
                {mitEinkauf && p && (<>
                  <td>{p.suppliers.length > 0 ? p.suppliers.join(", ") : ja(false)}</td>
                  <td>{p.exw ? `${formatNumber(p.exw.amount, i18n.lang)} ${p.exw.currency}` : ja(false)}</td>
                  <td>{p.tariff
                    ? <>{p.tariff.code}{p.tariff.inherited && <span className="mp__erbe"> {t("mp.inheritedShort")}</span>}</>
                    : ja(false)}</td>
                  <td>{p.packaging === "own" ? t("mp.packOwn")
                      : p.packaging === "model" ? <span className="mp__erbe">{t("mp.inheritedShort")}</span>
                      : ja(false)}</td>
                  <td data-testid="mp-kalkulierbar">{ja(p.basis.calculable)}</td>
                  <td>{p.basis.latest
                    ? <>{new Date(p.basis.latest.calculatedAt).toLocaleDateString(i18n.lang === "en" ? "en-GB" : "de-DE")}
                        {!p.basis.latest.complete && <span className="mp__erbe"> {t("mp.latestIncomplete")}</span>}</>
                    : <span className="models__none">{t("mp.noCalculation")}</span>}</td>
                  <td>{p.basis.latest?.complete && p.basis.latest.listPriceEur ? geld(p.basis.latest.listPriceEur) : "—"}</td>
                  <td data-testid="mp-freigegeben">{p.released
                    ? <span title={t("models.priceValidFrom", { date: p.released.validFrom })}>{geld(p.released.amountEur)}</span>
                    : "—"}</td>
                </>)}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

// ── Gemeinsame Daten ────────────────────────────────────────────────────────

/**
 * Was fuer alle Ausfuehrungen gilt -- in Karten, wie in der Produktakte.
 *
 * Jede Zeile sagt drei Dinge: den Wert am Modell, fuer wie viele Ausfuehrungen
 * er gilt, und welche einen eigenen Wert tragen. Traegt das Modell den Wert
 * noch nicht, alle Ausfuehrungen mit einem Wert aber denselben, bietet die
 * Zeile „Als gemeinsamen Wert übernehmen" an -- mit Vorschau und ausdruecklicher
 * Bestaetigung. Die Werte der Ausfuehrungen bleiben dabei stehen.
 */
function GemeinsamTab({ modelId, meta, rights, i18n, achsen, onChanged }: {
  modelId: number; meta: MetaResponse; rights: ModelRights; i18n: I18n;
  /** Die Merkmalscodes, die dieses Modell in Ausfuehrungen teilen. */
  achsen: readonly string[];
  onChanged: () => void;
}): React.JSX.Element {
  const t = i18n.t;
  const [daten, setDaten] = useState<ModelCommonDataResponse | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [vorschau, setVorschau] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const laeuft = useRef(false);

  const load = useCallback(async () => {
    setError(null);
    try { setDaten(await api.modelCommonData(modelId)); } catch (e) { setError(e); }
  }, [modelId]);
  useEffect(() => { void load(); }, [load]);

  async function uebernehmen(input: AdoptCommonValueRequest): Promise<void> {
    if (laeuft.current) return;
    laeuft.current = true;
    setBusy(true); setError(null);
    try {
      setDaten(await api.adoptCommonValue(modelId, input));
      setVorschau(null);
      onChanged();
    } catch (e) { setError(e); }
    finally { setBusy(false); laeuft.current = false; }
  }

  async function entfernen(definition: string, erben: number): Promise<void> {
    if (laeuft.current) return;
    laeuft.current = true;
    setBusy(true); setError(null);
    try {
      setDaten(await api.removeCommonValue(modelId, { kind: "property", definition, confirmLosing: erben }));
      setVorschau(null);
      onChanged();
    } catch (e) { setError(e); }
    finally { setBusy(false); laeuft.current = false; }
  }

  /*
   * Den doppelt gepflegten eigenen Wert abgeben (PM003-FACH-01).
   *
   * Derselbe Ablauf wie beim Entfernen des gemeinsamen Werts: dieselbe
   * Vorschau, dieselbe Sperre gegen den Doppelklick, dieselbe
   * Fehlerbehandlung. Nur die Richtung ist eine andere -- hier geht der
   * NAEHERE Wert, und der gemeinsame bleibt.
   */
  async function eigeneAbgeben(definition: string, articleIds: readonly number[]): Promise<void> {
    if (laeuft.current) return;
    laeuft.current = true;
    setBusy(true); setError(null);
    try {
      setDaten(await api.dropOwnValues(modelId, { kind: "property", definition, articleIds }));
      setVorschau(null);
      onChanged();
    } catch (e) { setError(e); }
    finally { setBusy(false); laeuft.current = false; }
  }

  if (error !== null && !daten) return <ErrorState message={errorText(i18n, error)} onRetry={load} />;
  if (!daten) return <LoadingState />;

  const def = (code: string) => meta.propertyDefinitions.find((d) => d.code === code);
  /*
   * AUCH DAS ABWEICHENDE BEKOMMT EINE ZEILE (003 V2).
   *
   * Bis hierher standen Merkmale, deren SKU verschiedene Werte tragen, nur
   * als Aufzaehlung in einer Fussnote. Damit fehlte genau dort eine
   * Entscheidung, wo eine ansteht: entweder ist es ein Ausfuehrungsmerkmal,
   * oder die Werte gehoeren angeglichen. Die Zeile steht jetzt da, ihr
   * Schalter ist gesperrt, und der Grund haengt am Schalter.
   */
  const gemeinsam = daten.properties.filter((p) => !p.differs || p.model.length > 0);
  const verschieden = daten.properties.filter((p) => p.differs && p.model.length === 0);
  const masse = gemeinsam.filter((p) => MASSE.has(p.definition));
  const zert = gemeinsam.filter((p) => !MASSE.has(p.definition) && def(p.definition)?.group === "konformitaet");
  const technik = [...gemeinsam, ...verschieden]
    .filter((p) => !MASSE.has(p.definition) && def(p.definition)?.group !== "konformitaet")
    .sort((a, b) => (def(a.definition)?.sortOrder ?? 0) - (def(b.definition)?.sortOrder ?? 0));
  const nummer = (id: number): string => daten.skus.find((s) => s.articleId === id)?.articleNumber ?? String(id);
  const n = daten.skus.length;

  /*
   * EINE ZEILE, DREI SPALTEN (Produktmodelle 003 V2).
   *
   *   EIGENSCHAFT | WERT / BEOBACHTETER WERT | GEMEINSAMER MODELLWERT
   *
   * Bis hierher wechselten sich je Merkmal Label, Wert, Herkunftshinweis
   * und ein Knopf ab; bei dreissig Merkmalen verlor man die Zeile. Jetzt
   * steht die ENTSCHEIDUNG immer an derselben Stelle -- rechts, als
   * Schalter --, und die Herkunft bleibt als leiser Zusatz in derselben
   * Zeile stehen.
   *
   * Der Schalter kennt drei Zustaende, und keiner davon luegt:
   *   aktiv        das Modell traegt den Wert; ein Klick leitet das
   *                Entfernen ein (mit Vorschau, wer ihn dann nicht mehr
   *                sieht).
   *   inaktiv      es gibt genau einen beobachteten Wert; ein Klick zeigt
   *                die Uebernahmevorschau.
   *   Ausfuehrung  das Merkmal unterscheidet die Ausfuehrungen gerade --
   *                dann ist es kein gemeinsamer Wert, sondern ihr
   *                Gegenteil. Kein Schalter, sondern eine Auskunft.
   */
  const zeile = (p: ModelCommonProperty): React.JSX.Element => {
    const d = def(p.definition);
    const eigen = p.perSku.filter((s) => s.own.length > 0);
    const modellText = wertText(d, p.model, i18n);
    const gleich = eigen.filter((s) => wertText(d, s.own, i18n) === modellText);
    const abweichend = eigen.filter((s) => wertText(d, s.own, i18n) !== modellText);
    const erben = p.perSku.filter((s) => s.own.length === 0);
    const key = `p:${p.definition}`;
    const achse = achsen.includes(p.definition);
    const aktiv = p.model.length > 0;
    /* Mehrere verschiedene beobachtete Werte: nicht blind aktivieren. */
    const beobachtet = [...new Set(eigen.map((s) => wertText(d, s.own, i18n)))];
    const eindeutig = beobachtet.length === 1;

    const status = achse ? (
      /*
       * EINE ACHSE MIT MODELLWERT IST EIN FEHLZUSTAND -- und bekommt den
       * Rueckweg, nicht nur eine Auskunft (Browserabnahme 003).
       *
       * Seit dieser Abnahme weist der Kern jeden neuen Modellwert auf einer
       * Achse zurueck. Was vorher entstand, steht aber noch da, und die
       * Auskunft „Ausfuehrungsmerkmal" allein liesse es dort: die Karte bot
       * hier bewusst keinen Schalter an, also gab es genau an der Stelle,
       * an der etwas zu berichtigen ist, keinen Weg. Der Knopf fuehrt in
       * dieselbe Entfernen-Vorschau wie ueberall sonst -- kein zweiter
       * Mechanismus, nur seine fehlende Tuer.
       */
      aktiv ? (
        <button type="button" className="mp__schalter mp__schalter--fehler" data-testid="mp-achsenfehler"
          data-zustand="achse-mit-wert" data-def={p.definition}
          disabled={!rights.mayWriteProps || busy}
          title={t("mp.axisHasModelValueTitle")}
          onClick={() => setVorschau(`x:${p.definition}`)}>
          <span className="mp__punktschalter">!</span> {t("mp.axisHasModelValue")}
        </button>
      ) : (
        <span className="mp__status--achse" data-testid="mp-status-achse">{t("mp.axisProperty")}</span>
      )
    ) : aktiv ? (
      <button type="button" className="mp__schalter mp__schalter--an" data-testid="mp-schalter"
        data-zustand="aktiv" data-def={p.definition}
        disabled={!rights.mayWriteProps || busy}
        title={t("mp.toggleOffTitle")}
        onClick={() => setVorschau(`x:${p.definition}`)}>
        <span className="mp__punktschalter">●</span> {t("mp.commonValue")}
      </button>
    ) : (
      <button type="button" className="mp__schalter" data-testid="mp-schalter"
        data-zustand={eigen.length === 0 ? "leer" : eindeutig ? "inaktiv" : "mehrdeutig"}
        data-def={p.definition}
        disabled={!rights.mayWriteProps || eigen.length === 0 || busy}
        title={eigen.length === 0 ? t("mp.toggleNoValue")
          : eindeutig ? t("mp.toggleOnTitle") : t("mp.toggleAmbiguous")}
        onClick={() => setVorschau(key)}>
        <span className="mp__punktschalter">○</span> {t("mp.commonValue")}
      </button>
    );

    return (
      <li key={p.definition} className="mp__zeile mp__zeile--merkmal"
          data-testid="mp-gemeinsamzeile" data-def={p.definition}>
        <span className="mp__zeilenlabel">{propertyLabel(i18n, d ?? { code: p.definition })}</span>
        <span className="mp__zeilenmitte">
          <span className="mp__zeilenwert">
            {aktiv ? modellText
              : eigen.length > 0 ? beobachtet.join(" · ")
              : <span className="models__none">{t("mp.notOnModel")}</span>}
          </span>
          {/* Die Herkunft -- leiser, aber in derselben Zeile. */}
          <span className="mp__abdeckung">
            {aktiv ? t("mp.coverage", { n: erben.length, total: n })
              : t("mp.onlyOnSkus", { n: eigen.length, total: n })}
            {/*
              * AUS DEM HINWEIS WIRD EIN WEG (PM003-FACH-01).
              *
              * „1 mit eigenem, gleichem Wert" stimmte, half aber nicht: die
              * SKU trug dieselbe Angabe ein zweites Mal, und wer spaeter den
              * Modellwert berichtigte, berichtigte sie nicht mit. Der Satz
              * steht weiter da -- jetzt als Knopf, der in dieselbe Vorschau
              * fuehrt wie jede andere Entscheidung dieser Karte.
              */}
            {aktiv && gleich.length > 0 && (
              rights.mayWriteProps ? (
                <button type="button" className="mp__eigen mp__eigen--knopf" data-testid="mp-eigengleich"
                  data-def={p.definition} disabled={busy} title={t("mp.dropOwnTitle")}
                  onClick={() => setVorschau(`e:${p.definition}`)}>
                  {" · "}{t("mp.ownSame", { n: gleich.length })}
                </button>
              ) : <span className="mp__eigen"> · {t("mp.ownSame", { n: gleich.length })}</span>
            )}
            {aktiv && abweichend.length > 0 && (
              <span className="mp__eigen mp__eigen--abweichend"> · {t("mp.ownDiffers", { n: abweichend.length })}{" "}
                ({abweichend.map((s) => `${nummer(s.articleId)}: ${wertText(d, s.own, i18n)}`).join(", ")})</span>
            )}
          </span>
        </span>
        <span className="mp__zeilenstatus">{status}</span>

        {vorschau === key && (
          <Vorschau i18n={i18n} busy={busy}
            kuenftig={eindeutig ? beobachtet[0]! : t("mp.chooseCandidate")}
            zeilen={p.perSku.map((s) => ({ nummer: nummer(s.articleId),
              eigen: s.own.length > 0 ? wertText(d, s.own, i18n) : null }))}
            hinweis={eindeutig ? null : t("mp.ambiguousNote")}
            onJa={eindeutig ? () => void uebernehmen({ kind: "property", definition: p.definition }) : null}
            onNein={() => setVorschau(null)} />
        )}
        {vorschau === `e:${p.definition}` && (
          /*
           * JEDE AUSFUEHRUNG EINZELN (PM003-FACH-01).
           *
           * „Alle auf einen Klick" waere hier falsch: dass zwei SKU heute
           * denselben Wert tragen, heisst nicht, dass beide ihn abgeben
           * sollen. Dieselben Kaestchen wie in der Altartikel-Uebernahme,
           * derselbe zugeknoepfte Knopf, solange nichts gewaehlt ist.
           */
          <EigenwertAbgabe modelId={modelId} definition={p.definition}
            i18n={i18n} busy={busy} kuenftig={modellText}
            onJa={(ids) => void eigeneAbgeben(p.definition, ids)}
            onNein={() => setVorschau(null)} />
        )}
        {vorschau === `x:${p.definition}` && (
          <Vorschau i18n={i18n} busy={busy} kuenftig={t("mp.removeResult")}
            zeilen={p.perSku.map((s) => ({ nummer: nummer(s.articleId),
              eigen: s.own.length > 0 ? wertText(d, s.own, i18n) : null }))}
            hinweis={erben.length > 0 ? t("mp.removeLosing", { n: erben.length }) : t("mp.removeNoLoss")}
            onJa={() => void entfernen(p.definition, erben.length)}
            onNein={() => setVorschau(null)} />
        )}
      </li>
    );
  };

  const packText = (p: ModelCommonPackaging | null): string => {
    if (!p) return "—";
    const m = p.master;
    const z = (v: string | null) => v === null ? "?" : formatNumber(v, i18n.lang);
    const teile = [
      m ? t("mp.packMaster", { l: z(m.lengthMm), b: z(m.widthMm), h: z(m.heightMm) }) : t("mp.packNoMaster"),
      m?.grossWeightKg ? `${z(m.grossWeightKg)} kg` : null,
      m ? t("mp.packUnits", { n: m.unitCount }) : null,
      p.materials.length > 0 ? p.materials.map((x) =>
        `${i18n.tCode("packMaterial", x.material.split(":")[1] ?? x.material)} ${formatNumber(x.weightG, i18n.lang)} g`).join(", ") : null,
    ].filter(Boolean);
    return `${t("mp.packRevision", { n: p.revision })} · ${teile.join(" · ")}`;
  };

  return (
    <div className="mp__tab mp__karten" data-testid="mp-gemeinsam-tab">
      <p className="models__note">{t("mp.commonNote")}</p>
      {error !== null && <p className="models__error" role="alert">{errorText(i18n, error)}</p>}

      <Karte titel={t("mp.cardMasse")}>
        {masse.length === 0 ? <p className="models__empty">{t("mp.cardEmpty")}</p>
          : <ul className="mp__zeilen">{masse.map(zeile)}</ul>}
      </Karte>

      <Karte titel={t("mp.cardVerpackung")}>
        <ul className="mp__zeilen">
          <li className="mp__zeile" data-testid="mp-verpackung">
            <span className="mp__zeilenlabel">{t("mp.onModel")}</span>
            <span className="mp__zeilenwert">{daten.packaging.model
              ? packText(daten.packaging.model) : <span className="models__none">{t("mp.notOnModel")}</span>}</span>
          </li>
          {daten.packaging.perSku.map((s) => (
            <li key={s.articleId} className="mp__zeile mp__zeile--sku">
              <span className="mp__zeilenlabel"><code>{nummer(s.articleId)}</code></span>
              <span className="mp__zeilenwert">{s.own ? `${t("mp.ownStand")}: ${packText(s.own)}`
                : daten.packaging.model ? <span className="mp__erbe">{t("mp.inheritsFromModel")}</span>
                : <span className="mp__punkt mp__punkt--fehlt">✕ {t("mp.none")}</span>}</span>
            </li>
          ))}
        </ul>
        {daten.packaging.candidateArticleId !== null && rights.mayWriteLogistics && vorschau !== "pack" && (
          <button className="btn btn--small mp__uebernehmen" onClick={() => setVorschau("pack")}>{t("mp.adoptPack")}</button>
        )}
        {vorschau === "pack" && daten.packaging.candidateArticleId !== null && (
          <Vorschau i18n={i18n} busy={busy}
            kuenftig={packText(daten.packaging.perSku.find((s) => s.articleId === daten.packaging.candidateArticleId)!.own)}
            zeilen={daten.packaging.perSku.map((s) => ({ nummer: nummer(s.articleId),
              eigen: s.own ? packText(s.own) : null }))}
            onJa={() => void uebernehmen({ kind: "packaging", articleId: daten.packaging.candidateArticleId! })}
            onNein={() => setVorschau(null)} />
        )}
      </Karte>

      <Karte titel={t("mp.cardZoll")}>
        <ul className="mp__zeilen">
          <li className="mp__zeile" data-testid="mp-zoll">
            <span className="mp__zeilenlabel">{t("mp.tariffOnModel")}</span>
            <span className="mp__zeilenwert">{daten.tariff.model ?? <span className="models__none">{t("mp.notOnModel")}</span>}</span>
            <span className="mp__abdeckung">
              {daten.tariff.model !== null
                ? t("mp.coverage", { n: daten.tariff.perSku.filter((s) => s.own === null).length, total: n })
                : t("mp.onlyOnSkus", { n: daten.tariff.perSku.filter((s) => s.own !== null).length, total: n })}
              {daten.tariff.perSku.some((s) => s.own !== null) && (
                <span className="mp__eigen"> · {t("mp.ownValues", { n: daten.tariff.perSku.filter((s) => s.own !== null).length })}{" "}
                  ({daten.tariff.perSku.filter((s) => s.own !== null).map((s) => `${nummer(s.articleId)}: ${s.own}`).join(", ")})</span>
              )}
            </span>
            {daten.tariff.candidate !== null && rights.mayWriteLogistics && vorschau !== "tarif" && (
              <button className="btn btn--small mp__uebernehmen" onClick={() => setVorschau("tarif")}>{t("mp.adopt")}</button>
            )}
            {vorschau === "tarif" && daten.tariff.candidate !== null && (
              <Vorschau i18n={i18n} busy={busy} kuenftig={daten.tariff.candidate}
                zeilen={daten.tariff.perSku.map((s) => ({ nummer: nummer(s.articleId), eigen: s.own }))}
                onJa={() => void uebernehmen({ kind: "tariff" })} onNein={() => setVorschau(null)} />
            )}
          </li>
        </ul>
      </Karte>

      <Karte titel={t("mp.cardTechnik")}>
        {technik.length === 0 && achsen.length === 0
          ? <p className="models__empty">{t("mp.cardEmpty")}</p>
          : (
            <ul className="mp__zeilen">
              {technik.map(zeile)}
              {/* Die Achsen stehen mit, damit niemand sie vermisst -- als
                  Auskunft, nicht als Entscheidung (003 V2). Nur die, die
                  nicht ohnehin schon eine Zeile haben: eine Achse mit
                  gepflegten SKU-Werten stand bis zur Browserabnahme 003
                  zweimal da, einmal mit Werten und einmal ohne. */}
              {achsen.filter((code) => !technik.some((p) => p.definition === code)).map((code) => (
                <li key={`achse-${code}`} className="mp__zeile mp__zeile--merkmal"
                    data-testid="mp-gemeinsamzeile" data-def={code}>
                  <span className="mp__zeilenlabel">
                    {propertyLabel(i18n, def(code) ?? { code })}
                  </span>
                  <span className="mp__zeilenmitte">
                    <span className="mp__zeilenwert">{t("mp.axisValues")}</span>
                    <span className="mp__abdeckung">{t("mp.axisWhere")}</span>
                  </span>
                  <span className="mp__zeilenstatus">
                    <span className="mp__status--achse" data-testid="mp-status-achse">{t("mp.axisProperty")}</span>
                  </span>
                </li>
              ))}
            </ul>
          )}
        {verschieden.length > 0 && (
          <p className="models__note" data-testid="mp-verschieden">
            {t("mp.differsNote", { list: verschieden.map((p) => i18n.tCode("prop", p.definition, p.definition)).join(", ") })}
          </p>
        )}
      </Karte>

      {/*
        * ALTARTIKEL-UEBERNAHME (003 V2).
        *
        * TP154 traegt seine Technik an der einen SKU, die es schon gab. Der
        * Weg dorthin ist kein Automatismus: Vorschau, Auswahl, dann erst
        * schreiben -- und Ausfuehrungsmerkmale sind nie dabei.
        */}
      {rights.mayManageCatalog && (
        <Karte titel={t("mk.catalog")}>
          <Merkmalsverwaltung meta={meta} i18n={i18n} mayDelete={rights.mayDeleteModels}
            onChanged={() => { void load(); onChanged(); }} />
        </Karte>
      )}

      {rights.mayWriteProps && (
        <Karte titel={t("mp.adoptFromArticle")}>
          <AltartikelUebernahme modelId={modelId} skus={daten.skus} meta={meta} i18n={i18n}
            onDone={() => { void load(); onChanged(); }} />
        </Karte>
      )}

      <Karte titel={t("mp.cardZert")}>
        {zert.length === 0 ? <p className="models__empty">{t("mp.cardEmpty")}</p>
          : <ul className="mp__zeilen">{zert.map(zeile)}</ul>}
      </Karte>

      <Karte titel={t("mp.cardNachweise")}>
        <p className="models__note" data-testid="mp-nachweise-hinweis">{t("mp.evidenceNotModel")}</p>
        <ul className="mp__zeilen">
          {daten.evidence.map((e) => (
            <li key={e.articleId} className="mp__zeile mp__zeile--sku">
              <span className="mp__zeilenlabel"><code>{nummer(e.articleId)}</code></span>
              <span className="mp__zeilenwert">{e.standards.length > 0 ? e.standards.join(" · ") : "—"}</span>
            </li>
          ))}
        </ul>
      </Karte>

      {/* Die bestehenden Editoren -- eingeklappt, aber vollstaendig. */}
      <details className="mp__pflege">
        <summary>{t("mp.editDirect")}</summary>
        <ProductProperties subject={{ kind: "model", id: modelId }} meta={meta}
          mayWrite={rights.mayWriteProps} mayConfirm={rights.mayConfirmProps} />
        <ModelLogistics modelId={modelId} meta={meta} mayWrite={rights.mayWriteLogistics} />
        <button className="btn btn--small" onClick={() => { void load(); onChanged(); }}>{t("mp.refresh")}</button>
      </details>
    </div>
  );
}

function Karte({ titel, children }: { titel: string; children: React.ReactNode }): React.JSX.Element {
  return (
    <section className="mp__karte">
      <h3 className="mp__kartenkopf">{titel}</h3>
      <div className="mp__karteninhalt">{children}</div>
    </section>
  );
}

/**
 * Die Vorschau einer Uebernahme -- was am Modell entsteht, und was an jeder
 * Ausfuehrung danach gilt. Nichts geschieht ohne „Übernehmen".
 */
/**
 * Der Merkmalskatalog, dort wo er gebraucht wird (Produktmodelle 003 V2).
 *
 * WARUM AN DIESER STELLE
 *
 * Wer ein Produktmodell pflegt, merkt GENAU HIER, dass ein Merkmal fehlt --
 * nicht in einer Verwaltung, die man erst suchen muss. Die vier Wege sind
 * dieselben wie bei Kategorie und Familie: anlegen, korrigieren,
 * nachsehen, wo es benutzt wird, und loeschen, wenn es nirgends benutzt
 * wird.
 *
 * WAS HIER NICHT PASSIERT
 *
 * Ein Merkmal aus DIESEM Modell zu entfernen ist etwas anderes als es
 * systemweit zu loeschen -- das erste tut der Statusschalter der Zeile
 * (gemeinsamer Wert aus), das zweite steht hier und geht nur, wenn
 * nirgends ein Wert daran haengt.
 */
function Merkmalsverwaltung({ meta, i18n, mayDelete, onChanged }: {
  meta: MetaResponse; i18n: I18n; mayDelete: boolean; onChanged: () => void;
}): React.JSX.Element {
  const t = i18n.t;
  /*
   * Die Nachschlagedaten laden beim Seitenwechsel; ein eben angelegtes
   * Merkmal steht dort noch nicht. Es hier zu ergaenzen ist keine zweite
   * Wahrheit, sondern dieselbe Liste eine Sekunde frueher -- dieselbe
   * Loesung wie bei der neuen Produktfamilie im Anlageflow.
   */
  const [dazu, setDazu] = useState<readonly { code: string; nameDe: string | null; nameEn: string | null }[]>([]);
  const [fort, setFort] = useState<readonly string[]>([]);
  const [offen, setOffen] = useState<"neu" | "pflege" | null>(null);
  const [code, setCode] = useState("");
  const [nameDe, setNameDe] = useState("");
  const [nameEn, setNameEn] = useState("");
  const [gruppe, setGruppe] = useState<PropertyGroup>("elektrik");
  const [typ, setTyp] = useState<PropertyValueType>("range");
  const [einheit, setEinheit] = useState("");
  const [aehnlich, setAehnlich] = useState<readonly { code: string; nameDe: string | null; inUse: boolean }[]>([]);
  const [gewaehlt, setGewaehlt] = useState<string>("");
  const [verwendung, setVerwendung] = useState<PropertyDefinitionUsage | null>(null);
  const [bestaetigung, setBestaetigung] = useState("");
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);
  const laeuft = useRef(false);

  /* Die Dublettenwarnung entsteht beim Tippen -- nicht beim Speichern. */
  useEffect(() => {
    if (offen !== "neu" || (code.trim() === "" && nameDe.trim() === "")) { setAehnlich([]); return; }
    let gilt = true;
    const zeit = window.setTimeout(() => {
      void api.similarDefinitions(code.trim(), nameDe.trim())
        .then((r) => { if (gilt) setAehnlich(r.items); })
        .catch(() => { if (gilt) setAehnlich([]); });
    }, 250);
    return () => { gilt = false; window.clearTimeout(zeit); };
  }, [offen, code, nameDe]);

  useEffect(() => {
    if (gewaehlt === "") { setVerwendung(null); return; }
    let gilt = true;
    void api.propertyDefinitionUsage(gewaehlt)
      .then((v) => { if (gilt) { setVerwendung(v); setNameDe(v.nameDe ?? ""); setNameEn(v.nameEn ?? ""); setGruppe(v.group); } })
      .catch((e) => { if (gilt) setError(e); });
    return () => { gilt = false; };
  }, [gewaehlt]);

  async function tun(fn: () => Promise<unknown>): Promise<void> {
    if (laeuft.current) return;
    laeuft.current = true; setBusy(true); setError(null);
    try { await fn(); onChanged(); }
    catch (e) { setError(e); }
    finally { setBusy(false); laeuft.current = false; }
  }

  return (
    <div data-testid="mk-katalog">
      <p className="models__note">{t("mk.catalogNote")}</p>
      <div className="feldzeile">
        <button className="btn btn--small" data-testid="mk-neu"
          onClick={() => { setOffen(offen === "neu" ? null : "neu"); setError(null); }}>
          {t("mk.newProperty")}
        </button>
        <label className="feld feld--kurz">
          <span className="feld__label">{t("mk.manageExisting")}</span>
          <select className="feld__select" data-testid="mk-auswahl" value={gewaehlt}
                  onChange={(e) => { setGewaehlt(e.target.value); setOffen(e.target.value === "" ? null : "pflege"); setError(null); }}>
            <option value="">{t("models.choose")}</option>
            {[...meta.propertyDefinitions.filter((d) => !fort.includes(d.code)), ...dazu]
              .sort((a, b) => propertyLabel(i18n, a).localeCompare(propertyLabel(i18n, b), i18n.lang))
              .map((d) => (
                <option key={d.code} value={d.code}>{propertyLabel(i18n, d)}</option>
              ))}
          </select>
        </label>
      </div>

      {offen === "neu" && (
        <div className="models__form models__form--wide" data-testid="mk-neuform">
          <div className="feldzeile">
            <label className="feld feld--kurz">
              <span className="feld__label">{t("mk.code")}</span>
              <input className="feld__input" data-testid="mk-code" value={code} maxLength={60}
                onChange={(e) => setCode(e.target.value)} />
              <small className="feld__note">{t("mk.codeHint")}</small>
            </label>
            <label className="feld feld--kurz">
              <span className="feld__label">{t("mk.nameDe")}</span>
              <input className="feld__input" data-testid="mk-name" value={nameDe} maxLength={120}
                onChange={(e) => setNameDe(e.target.value)} />
            </label>
            <label className="feld feld--kurz">
              <span className="feld__label">{t("mk.nameEn")}</span>
              <input className="feld__input" value={nameEn} maxLength={120}
                onChange={(e) => setNameEn(e.target.value)} />
            </label>
          </div>
          <div className="feldzeile">
            <label className="feld feld--kurz">
              <span className="feld__label">{t("mk.group")}</span>
              <select className="feld__select" value={gruppe}
                      onChange={(e) => setGruppe(e.target.value as PropertyGroup)}>
                {PROPERTY_GROUPS.map((g) => (
                  <option key={g} value={g}>{i18n.tCode("propGroup", g, g)}</option>
                ))}
              </select>
            </label>
            <label className="feld feld--kurz">
              <span className="feld__label">{t("mk.valueType")}</span>
              <select className="feld__select" data-testid="mk-typ" value={typ}
                      onChange={(e) => setTyp(e.target.value as PropertyValueType)}>
                {/* Die Schluessel stehen woertlich da -- ein zusammengesetzter
                    Schluessel waere fuer checki18n unsichtbar. */}
                <option value="range">{t("mk.type.range")}</option>
                <option value="integer">{t("mk.type.integer")}</option>
                <option value="decimal">{t("mk.type.decimal")}</option>
                <option value="text">{t("mk.type.text")}</option>
                <option value="boolean">{t("mk.type.boolean")}</option>
              </select>
            </label>
            <label className="feld feld--kurz">
              <span className="feld__label">{t("mk.unit")}</span>
              <input className="feld__input" data-testid="mk-einheit" value={einheit} maxLength={12}
                disabled={!["range", "integer", "decimal"].includes(typ)}
                onChange={(e) => setEinheit(e.target.value)} />
              <small className="feld__note">{t("mk.unitHint")}</small>
            </label>
          </div>
          {aehnlich.length > 0 && (
            <p className="models__note models__note--strong" data-testid="mk-dublette">
              {t("mk.similar")}{" "}
              {aehnlich.map((x) => `${x.nameDe ?? x.code} (${x.code}${x.inUse ? t("mk.similarInUse") : ""})`).join(", ")}
            </p>
          )}
          <div className="models__formactions">
            <button className="btn btn--primary" data-testid="mk-anlegen"
              disabled={busy || code.trim() === "" || nameDe.trim() === ""}
              onClick={() => void tun(async () => {
                const angelegt = await api.createPropertyDefinition({
                  code: code.trim(), nameDe: nameDe.trim(),
                  nameEn: nameEn.trim() === "" ? null : nameEn.trim(),
                  group: gruppe, valueType: typ,
                  unit: einheit.trim() === "" ? null : einheit.trim(),
                });
                setDazu((d) => [...d, { code: angelegt.code, nameDe: angelegt.nameDe, nameEn: angelegt.nameEn }]);
                setOffen(null); setCode(""); setNameDe(""); setNameEn(""); setEinheit("");
              })}>{t("mk.create")}</button>
            <button className="btn" onClick={() => { setOffen(null); setError(null); }}>{t("common.cancel")}</button>
          </div>
        </div>
      )}

      {offen === "pflege" && verwendung && (
        <div className="models__form models__form--wide" data-testid="mk-pflegeform">
          <p className="models__note" data-testid="mk-verwendung">{verwendung.reasons.join(" ")}</p>
          <div className="feldzeile">
            <label className="feld feld--kurz">
              <span className="feld__label">{t("mk.nameDe")}</span>
              <input className="feld__input" data-testid="mk-pflegename" value={nameDe} maxLength={120}
                onChange={(e) => setNameDe(e.target.value)} />
            </label>
            <label className="feld feld--kurz">
              <span className="feld__label">{t("mk.group")}</span>
              <select className="feld__select" value={gruppe}
                      onChange={(e) => setGruppe(e.target.value as PropertyGroup)}>
                {PROPERTY_GROUPS.map((g) => (
                  <option key={g} value={g}>{i18n.tCode("propGroup", g, g)}</option>
                ))}
              </select>
            </label>
            <span className="feld feld--kurz">
              <span className="feld__label">{t("mk.structure")}</span>
              <span className="models__note" data-testid="mk-struktur">
                {verwendung.valueType}{verwendung.unit ? ` · ${verwendung.unit}` : ""}
                {" · "}{verwendung.mayChangeStructure ? t("mk.structureFree") : t("mk.structureLocked")}
              </span>
            </span>
          </div>
          <div className="models__formactions">
            <button className="btn btn--primary" data-testid="mk-speichern" disabled={busy || nameDe.trim() === ""}
              onClick={() => void tun(async () => {
                setVerwendung(await api.updatePropertyDefinition(verwendung.code, {
                  nameDe: nameDe.trim(), nameEn: nameEn.trim() === "" ? null : nameEn.trim(), group: gruppe,
                }));
              })}>{t("common.save")}</button>
            <button className="btn" onClick={() => { setOffen(null); setGewaehlt(""); }}>{t("common.cancel")}</button>
          </div>
          {mayDelete && (
            <div className="sl__gefahr">
              {!verwendung.mayDelete ? (
                <p className="models__note models__note--strong" data-testid="mk-loeschgesperrt">
                  {verwendung.reasons.join(" ")}
                </p>
              ) : (
                <div className="feldzeile">
                  <label className="feld feld--kurz">
                    <span className="feld__label">{t("mk.confirmLabel", { code: verwendung.code })}</span>
                    <input className="feld__input" data-testid="mk-bestaetigung" value={bestaetigung}
                      onChange={(e) => setBestaetigung(e.target.value)} />
                  </label>
                  <button className="btn btn--small btn--danger" data-testid="mk-loeschen"
                    disabled={busy || bestaetigung.trim() !== verwendung.code}
                    onClick={() => void tun(async () => {
                      await api.deletePropertyDefinition(verwendung.code, bestaetigung.trim());
                      setFort((f) => [...f, verwendung.code]);
                      setDazu((d) => d.filter((x) => x.code !== verwendung.code));
                      setOffen(null); setGewaehlt(""); setBestaetigung("");
                    })}>{t("mk.delete")}</button>
                </div>
              )}
            </div>
          )}
        </div>
      )}
      {error !== null && <p className="models__error" role="alert">{errorText(i18n, error)}</p>}
    </div>
  );
}

/**
 * Werte eines bestehenden Artikels als gemeinsame Modellwerte uebernehmen
 * (Produktmodelle 003 V2).
 *
 * Drei Schritte, und keiner davon schreibt ungefragt: Artikel waehlen,
 * Vorschau lesen, auswaehlen. Was nicht vorschlagbar ist, steht mit Grund
 * daneben -- Ausfuehrungsmerkmal, das Modell sagt schon etwas, oder auf
 * Modellebene nicht gefuehrt. Angehakt ist zu Beginn nichts.
 */
function AltartikelUebernahme({ modelId, skus, meta, i18n, onDone }: {
  modelId: number;
  skus: readonly { articleId: number; articleNumber: string }[];
  meta: MetaResponse; i18n: I18n; onDone: () => void;
}): React.JSX.Element {
  const t = i18n.t;
  const [artikel, setArtikel] = useState<number | "">(skus.length === 1 ? skus[0]!.articleId : "");
  const [v, setV] = useState<AdoptFromArticlePreview | null>(null);
  const [wahl, setWahl] = useState<readonly string[]>([]);
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);
  const [ergebnis, setErgebnis] = useState<string | null>(null);
  const laeuft = useRef(false);

  async function vorschau(): Promise<void> {
    if (artikel === "") return;
    setBusy(true); setError(null); setErgebnis(null);
    try { setV(await api.adoptPreview(modelId, Number(artikel))); setWahl([]); }
    catch (e) { setError(e); } finally { setBusy(false); }
  }
  async function uebernehmen(): Promise<void> {
    if (laeuft.current || !v || wahl.length === 0) return;
    laeuft.current = true; setBusy(true); setError(null);
    try {
      const r = await api.adoptFromArticle(modelId, { articleId: v.article.id, definitions: wahl });
      setErgebnis(t("mp.adoptedCount", { n: r.adopted.length }));
      setV(null); setWahl([]);
      onDone();
    } catch (e) { setError(e); }
    finally { setBusy(false); laeuft.current = false; }
  }
  const label = (code: string): string =>
    propertyLabel(i18n, meta.propertyDefinitions.find((d) => d.code === code) ?? { code });
  const grund = (r: AdoptFromArticlePreview["items"][number]["reason"]): string =>
    r === "variant_axis" ? t("mp.adoptWhyAxis")
    : r === "model_has_own" ? t("mp.adoptWhyModel")
    : t("mp.adoptWhyLevel");
  const vorschlagbar = (v?.items ?? []).filter((x) => x.proposable);
  const uebrig = (v?.items ?? []).filter((x) => !x.proposable);

  return (
    <div data-testid="mp-altuebernahme">
      <p className="models__note">{t("mp.adoptFromArticleNote")}</p>
      <div className="feldzeile">
        <label className="feld feld--kurz">
          <span className="feld__label">{t("mp.adoptSource")}</span>
          <select className="feld__select" data-testid="mp-altquelle" value={artikel}
                  onChange={(e) => { setArtikel(e.target.value === "" ? "" : Number(e.target.value)); setV(null); }}>
            <option value="">{t("models.choose")}</option>
            {skus.map((s) => <option key={s.articleId} value={s.articleId}>{s.articleNumber}</option>)}
          </select>
        </label>
        <button className="btn btn--small mp__altvorschau" data-testid="mp-altvorschau"
          disabled={artikel === "" || busy} onClick={() => void vorschau()}>{t("mp.adoptAnalyse")}</button>
      </div>

      {ergebnis !== null && <p className="models__note" data-testid="mp-altergebnis">{ergebnis}</p>}

      {v && (
        <div className="mp__vorschau" data-testid="mp-altvorschau-liste">
          <p className="mp__vorschautitel">
            <strong>{t("mp.adoptCandidates", { n: vorschlagbar.length, number: v.article.articleNumber })}</strong>
          </p>
          {vorschlagbar.length === 0 && <p className="models__empty">{t("mp.adoptNothing")}</p>}
          <ul className="mp__vorschauliste">
            {vorschlagbar.map((x) => (
              <li key={x.definition}>
                <label className="models__check">
                  <input type="checkbox" data-testid="mp-altwahl" data-def={x.definition}
                    checked={wahl.includes(x.definition)}
                    onChange={(e) => setWahl((w) => e.target.checked
                      ? [...w, x.definition] : w.filter((c) => c !== x.definition))} />
                  {label(x.definition)}
                  {x.values > 1 && <span className="models__note"> ({t("mp.adoptValues", { n: x.values })})</span>}
                </label>
              </li>
            ))}
          </ul>
          {uebrig.length > 0 && (
            <p className="models__note" data-testid="mp-altnicht">
              {t("mp.adoptSkipped")}{" "}
              {uebrig.map((x) => `${label(x.definition)} (${grund(x.reason)})`).join(", ")}
            </p>
          )}
          <div className="models__formactions">
            <button className="btn btn--primary mp__altuebernehmen" data-testid="mp-altuebernehmen"
              disabled={busy || wahl.length === 0} onClick={() => void uebernehmen()}>
              {t("mp.adoptSelected", { n: wahl.length })}
            </button>
            <button className="btn" disabled={busy} onClick={() => { setV(null); setWahl([]); }}>
              {t("common.cancel")}
            </button>
          </div>
        </div>
      )}
      {error !== null && <p className="models__error" role="alert">{errorText(i18n, error)}</p>}
    </div>
  );
}

function Vorschau({ i18n, busy, kuenftig, zeilen, hinweis = null, onJa, onNein }: {
  i18n: I18n; busy: boolean; kuenftig: string;
  zeilen: readonly { nummer: string; eigen: string | null }[];
  /** Was dabei zu bedenken ist -- etwa: wer den Wert danach nicht mehr zeigt. */
  hinweis?: string | null;
  /** null: es gibt nichts zu bestaetigen (mehrere Kandidaten). */
  onJa: (() => void) | null;
  onNein: () => void;
}): React.JSX.Element {
  const t = i18n.t;
  return (
    <div className="mp__vorschau" role="group" aria-label={t("mp.adoptTitle")} data-testid="mp-vorschau">
      <p className="mp__vorschautitel"><strong>{t("mp.adoptTitle")}</strong></p>
      <p>{t("mp.adoptFuture")} <strong>{kuenftig}</strong></p>
      <ul className="mp__vorschauliste">
        {zeilen.map((z) => (
          <li key={z.nummer}>
            <code>{z.nummer}</code>{" "}
            {z.eigen !== null ? t("mp.adoptKeepsOwn", { value: z.eigen }) : t("mp.adoptInherits")}
          </li>
        ))}
      </ul>
      {hinweis !== null && (
        <p className="models__note models__note--strong" data-testid="mp-vorschauhinweis">{hinweis}</p>
      )}
      <p className="models__note">{t("mp.adoptNote")}</p>
      <div className="models__formactions">
        {onJa !== null && (
          <button className="btn btn--primary mp__uebernehmenja" disabled={busy} onClick={onJa}>{t("mp.adoptConfirm")}</button>
        )}
        <button className="btn" disabled={busy} onClick={onNein}>{t("common.cancel")}</button>
      </div>
    </div>
  );
}

/**
 * Die Abgabe doppelt gepflegter eigener Werte -- Ausfuehrung fuer Ausfuehrung
 * (PM003-FACH-01).
 *
 * Sie sieht aus wie die Vorschau daneben und verhaelt sich wie die
 * Altartikel-Uebernahme: was geschieht, steht vorher da, und es geschieht
 * nur mit dem, was jemand angekreuzt hat. Vorgewaehlt ist nichts -- eine
 * Loeschung, die man nur noch bestaetigt, ist keine Entscheidung.
 */
function EigenwertAbgabe({ modelId, definition, i18n, busy, kuenftig, onJa, onNein }: {
  modelId: number; definition: string;
  i18n: I18n; busy: boolean; kuenftig: string;
  onJa: (articleIds: readonly number[]) => void;
  onNein: () => void;
}): React.JSX.Element {
  const t = i18n.t;
  const [wahl, setWahl] = useState<number[]>([]);
  const [v, setV] = useState<DropOwnPreview | null>(null);
  const [fehler, setFehler] = useState<unknown>(null);
  useEffect(() => {
    let weg = false;
    void (async () => {
      try { const r = await api.dropOwnPreview(modelId, definition); if (!weg) setV(r); }
      catch (e) { if (!weg) setFehler(e); }
    })();
    return () => { weg = true; };
  }, [modelId, definition]);

  if (fehler !== null) {
    return (
      <div className="mp__vorschau" data-testid="mp-abgabe">
        <p className="models__error" role="alert">{errorText(i18n, fehler)}</p>
        <div className="models__formactions">
          <button className="btn" onClick={onNein}>{t("common.cancel")}</button>
        </div>
      </div>
    );
  }
  if (!v) return <div className="mp__vorschau" data-testid="mp-abgabe"><LoadingState /></div>;

  const beleg = (e: DropOwnEvidence): string =>
    `${i18n.tCode("quality", e.quality, e.quality)} · ${i18n.tCode("textOrigin", e.origin, e.origin)}`;
  const offen = v.candidates.filter((k) => k.verdict === "allowed" || k.verdict === "origin_lost");
  const gewaehlt = v.candidates.filter((k) => wahl.includes(k.articleId));

  return (
    <div className="mp__vorschau" role="group" aria-label={t("mp.dropOwnTitel")} data-testid="mp-abgabe">
      <p className="mp__vorschautitel"><strong>{t("mp.dropOwnTitel")}</strong></p>
      <p>{t("mp.dropOwnFuture")} <strong>{kuenftig}</strong></p>
      <ul className="mp__vorschauliste mp__abgabeliste">
        {v.candidates.map((k) => {
          const gesperrt = k.verdict === "quality_would_drop"
            || k.verdict === "clarification_or_proposal_present";
          /*
           * EINE ZEILE SAGT DREI DINGE (PM003-FACH-02): wer, was er abgibt
           * und was er dafuer bekommt. Der Grund einer Sperre steht darunter
           * im Klartext -- nicht als Zeichen, das man deuten muss.
           */
          return (
            <li key={k.articleId} className="mp__abgabezeile"
                data-testid="mp-abgabezeile" data-sku={k.articleId} data-urteil={k.verdict}>
              <label className="models__check">
                <input type="checkbox" data-testid="mp-abgabewahl" data-sku={k.articleId}
                  disabled={gesperrt || busy} checked={wahl.includes(k.articleId)}
                  onChange={(e) => setWahl((w) => e.target.checked
                    ? [...w, k.articleId] : w.filter((x) => x !== k.articleId))} />
                <code>{k.articleNumber}</code>{" "}
                <span className="mp__abgabebeleg">{beleg(k.own)} → {beleg(k.model)}</span>
              </label>
              {k.verdict === "origin_lost" && (
                <p className="models__note mp__abgabewarnung" data-testid="mp-abgabewarnung">
                  {t("mp.dropOwnOriginLost", {
                    eigen: beleg(k.own), modell: beleg(k.model) })}
                  {k.own.sourceSegment !== null && (
                    <> {t("mp.dropOwnSegmentLost", { segment: k.own.sourceSegment })}</>
                  )}
                </p>
              )}
              {k.verdict === "quality_would_drop" && (
                <p className="models__note models__note--strong mp__abgabesperre"
                   data-testid="mp-abgabesperre">{t("mp.dropOwnQualityBlocked")}</p>
              )}
              {k.verdict === "clarification_or_proposal_present" && (
                <p className="models__note models__note--strong mp__abgabesperre"
                   data-testid="mp-abgabesperre">
                  {k.clarifications > 0 ? t("mp.dropOwnClarificationBlocked")
                    : t("mp.dropOwnProposalBlocked")}
                </p>
              )}
            </li>
          );
        })}
      </ul>
      {offen.length === 0 && (
        <p className="models__empty" data-testid="mp-abgabekeine">{t("mp.dropOwnNoneFree")}</p>
      )}
      <p className="models__note models__note--strong" data-testid="mp-abgabehinweis">
        {t("mp.dropOwnNote", { n: wahl.length,
          list: gewaehlt.map((k) => k.articleNumber).join(", ") || "—" })}
      </p>
      <div className="models__formactions">
        <button className="btn btn--primary mp__abgebenja" data-testid="mp-abgeben"
          disabled={busy || wahl.length === 0} onClick={() => onJa(wahl)}>
          {t("mp.dropOwnConfirm")}
        </button>
        <button className="btn" disabled={busy} onClick={onNein}>{t("common.cancel")}</button>
      </div>
    </div>
  );
}

// ── Ausfuehrungen ───────────────────────────────────────────────────────────

function AusfuehrungenTab({ model, overview, meta, rights, i18n, onChanged, onError, onOpenArticle, onAssign, aktionen }: {
  model: ProductModelDetail; overview: ModelOverviewResponse | null; meta: MetaResponse;
  rights: ModelRights; i18n: I18n;
  onChanged: (m: ProductModelDetail) => void; onError: (e: unknown) => void;
  onOpenArticle: (id: number) => void;
  onAssign: ((variantId: number) => void) | null;
  aktionen: Ausfuehrungsaktionen | null;
}): React.JSX.Element {
  const t = i18n.t;
  return (
    <div className="mp__tab">
      {overview && <Ausfuehrungsmatrix overview={overview} meta={meta} i18n={i18n} onOpenArticle={onOpenArticle} onAssign={onAssign} aktionen={aktionen} />}
      {overview && overview.deviations.length > 0 && (
        <section className="models__block">
          <h3 className="detail__section">{t("mp.deviationsTitle")}</h3>
          <p className="models__note">{t("mp.deviationsNote")}</p>
          <div className="mp__tabellenrahmen">
            <table className="mp__table mp__abweichungen" data-testid="mp-abweichungen">
              <thead>
                <tr>
                  <th>{t("mp.colProperty")}</th>
                  {overview.deviations[0]!.perArticle.map((p) => (
                    <th key={p.articleId}><code>{artikelNummer(overview, p.articleId)}</code></th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {overview.deviations.map((d) => {
                  const def = meta.propertyDefinitions.find((x) => x.code === d.definition);
                  return (
                    <tr key={d.definition} data-def={d.definition}>
                      <th>{i18n.tCode("prop", d.definition, d.definition)}</th>
                      {d.perArticle.map((p) => <td key={p.articleId}>{wertText(def, p.values, i18n)}</td>)}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {overview && <GemeinsameAchsen overview={overview} i18n={i18n} />}
      <ModelVariantList model={model} mayWrite={rights.mayWriteModels}
        hiddenDimensions={new Set(overview?.axes.filter((a) => a.role === "common").map((a) => a.dimensionId) ?? [])}
        onAssign={onAssign ?? undefined}
        onChanged={onChanged} onError={onError} onOpenArticle={onOpenArticle} />

      {model.placeholders.length > 0 && (
        <section className="models__block">
          <h3 className="detail__section">{t("models.placeholders")}</h3>
          <p className="models__note models__note--strong">{t("models.placeholderNote")}</p>
          <ul className="models__articles">
            {model.placeholders.map((a) => (
              <li key={a.id} className="models__article models__article--placeholder">
                <button className="models__link" onClick={() => onOpenArticle(a.id)}><code>{a.articleNumber}</code></button>
                <span className="badge badge--placeholder" title={a.placeholderNote ?? ""}>{t("models.placeholderBadge")}</span>
              </li>
            ))}
          </ul>
        </section>
      )}

      {model.otherArticles.length > 0 && (
        <section className="models__block">
          <h3 className="detail__section">{t("models.otherArticles")}</h3>
          <ul className="models__articles">
            {model.otherArticles.map((a) => (
              <li key={a.id} className="models__article">
                <button className="models__link" onClick={() => onOpenArticle(a.id)}><code>{a.articleNumber}</code></button>
                <span className="models__artroles">
                  {a.roles.map((r) => <RoleBadge key={r.code} label={i18n.tCode("articleRole", r.code, r.name)} />)}
                </span>
              </li>
            ))}
          </ul>
        </section>
      )}

      {/* Der Achseneditor -- nicht mehr der Einstieg, aber vollstaendig. */}
      <details className="mp__pflege" data-testid="mp-achseneditor">
        <summary>{t("mp.editAxes")}</summary>
        <ModelDimensions model={model} definitions={meta.propertyDefinitions}
          mayWrite={rights.mayWriteModels} onChanged={onChanged} onError={onError} />
      </details>
    </div>
  );
}

// ── Texte & Dokumente ───────────────────────────────────────────────────────

function InhaltTab({ modelId, meta, rights, i18n }: {
  modelId: number; meta: MetaResponse; rights: ModelRights; i18n: I18n;
}): React.JSX.Element {
  const t = i18n.t;
  const [bearbeiten, setBearbeiten] = useState(false);
  const steuerung = useRef<DokumenteSteuerung | null>(null);
  return (
    <div className="mp__tab">
      <ModelTexts modelId={modelId} meta={meta} mayWrite={rights.mayWriteTexts} mayRelease={rights.mayReleaseTexts} />
      <section className="models__block" data-testid="mp-dokumente">
        <div className="mp__abschnittkopf">
          <h3 className="detail__section">{t("mp.documentsTitle")}</h3>
          {rights.mayWriteDocuments && !bearbeiten && (
            <button className="btn btn--small mp__dokbearbeiten" onClick={() => setBearbeiten(true)}>{t("mp.editDocuments")}</button>
          )}
        </div>
        <p className="models__note">{t("mp.documentsNote")}</p>
        <ArticleDocuments articleId={0} modelId={modelId} mayWrite={rights.mayWriteDocuments}
          editing={bearbeiten} onEditingChange={setBearbeiten} steuerung={steuerung} />
      </section>
    </div>
  );
}

// ── Verlauf ─────────────────────────────────────────────────────────────────

function VerlaufTab({ modelId, i18n }: { modelId: number; i18n: I18n }): React.JSX.Element {
  const t = i18n.t;
  const [daten, setDaten] = useState<ModelHistoryResponse | null>(null);
  const [error, setError] = useState<unknown>(null);
  useEffect(() => { api.modelHistory(modelId).then(setDaten).catch(setError); }, [modelId]);
  if (error !== null) return <ErrorState message={errorText(i18n, error)} />;
  if (!daten) return <LoadingState />;
  if (daten.items.length === 0) return <p className="models__empty">{t("mp.historyEmpty")}</p>;
  const zeit = (iso: string): string => new Date(iso).toLocaleString(i18n.lang === "en" ? "en-GB" : "de-DE",
    { dateStyle: "short", timeStyle: "short", timeZone: "Europe/Berlin" });
  return (
    <div className="mp__tab mp__tabellenrahmen">
      <table className="mp__table mp__verlauf" data-testid="mp-verlauf">
        <thead>
          <tr>
            <th>{t("mp.colWhen")}</th><th>{t("mp.colWho")}</th><th>{t("mp.colArea")}</th>
            <th>{t("mp.colAction")}</th><th>{t("mp.colChange")}</th>
          </tr>
        </thead>
        <tbody>
          {daten.items.map((h) => (
            <tr key={h.id}>
              <td className="mp__zeit">{zeit(h.changedAt)}</td>
              <td>{h.changedBy ?? "—"}</td>
              <td>{i18n.tCode("mpArea", h.area, h.area)}</td>
              <td>{i18n.tCode("modelLog", h.field, h.field)}</td>
              <td className="mp__aenderung">
                {h.oldValue && h.newValue ? `${h.oldValue} → ${h.newValue}` : h.newValue ?? h.oldValue ?? ""}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
